/**
 * Minimum time the branded loader stays up for each entry into the app.
 * The timer starts with the transition and runs alongside the real work, so the
 * loader shows for max(minimum, readiness) and never adds time after readiness.
 */
export const FEEDBOT_LOADER_TIMING = {
  loginToSwitcherMs: 2_000,
  switcherToWorkspaceMs: 2_000,
  pageRefreshMs: 2_000,
  loginPageRefreshMs: 2_000,
  organizationSwitchMs: 2_000,
} as const;

export type FeedBotLoaderTiming = {
  readonly [Key in keyof typeof FEEDBOT_LOADER_TIMING]: number;
};

export type LoaderHoldKind =
  | "loginToSwitcher"
  | "switcherToWorkspace"
  | "pageRefresh"
  | "loginPageRefresh"
  /** Sidebar switcher: another organization, or between a workspace and Super Admin. */
  | "organizationSwitch";

const TIMING_KEY_BY_KIND: Record<LoaderHoldKind, keyof FeedBotLoaderTiming> = {
  loginToSwitcher: "loginToSwitcherMs",
  switcherToWorkspace: "switcherToWorkspaceMs",
  pageRefresh: "pageRefreshMs",
  loginPageRefresh: "loginPageRefreshMs",
  organizationSwitch: "organizationSwitchMs",
};

export function loaderHoldMinimumMs(
  kind: LoaderHoldKind,
  timing: FeedBotLoaderTiming = FEEDBOT_LOADER_TIMING,
): number {
  return timing[TIMING_KEY_BY_KIND[kind]];
}

export interface LoaderHold {
  readonly kind: LoaderHoldKind;
  /** When the entry began (same clock as `now`). */
  readonly startedAt: number;
  /** When the minimum duration is satisfied. */
  readonly endsAt: number;
  readonly minimumElapsed: boolean;
}

export interface LoaderHoldConditions {
  /** The existing readiness checks for the destination have all passed. */
  destinationReady: boolean;
  /** Sign-in failed, the session ended, or bootstrap errored. Never held. */
  failed: boolean;
  /**
   * A 404 or 403 page is mounted, so routing and authorization already decided
   * the destination. Shown at once, never held. Does not affect `booting`.
   */
  confirmedErrorRoute?: boolean;
}

/**
 * Begins a hold at `now`. A hold whose minimum is still running is kept, so one
 * entry never stacks a second wait on top of the first.
 */
export function startLoaderHold(
  current: LoaderHold | null,
  kind: LoaderHoldKind,
  now: number,
  timing: FeedBotLoaderTiming = FEEDBOT_LOADER_TIMING,
): LoaderHold {
  if (current !== null && !current.minimumElapsed) return current;
  return {
    kind,
    startedAt: now,
    endsAt: now + loaderHoldMinimumMs(kind, timing),
    minimumElapsed: false,
  };
}

/** Marks the minimum as satisfied, ignoring timers from a replaced hold. */
export function markLoaderHoldElapsed(hold: LoaderHold | null, endsAt: number): LoaderHold | null {
  if (hold === null || hold.endsAt !== endsAt || hold.minimumElapsed) return hold;
  return { ...hold, minimumElapsed: true };
}

/**
 * A Login refresh whose session turns out to be authenticated follows the
 * authenticated page-refresh timing, measured from the same start. One wait.
 */
export function settleLoginPageRefreshHold(
  hold: LoaderHold | null,
  now: number,
  timing: FeedBotLoaderTiming = FEEDBOT_LOADER_TIMING,
): LoaderHold | null {
  if (hold === null || hold.kind !== "loginPageRefresh") return hold;
  const endsAt = hold.startedAt + loaderHoldMinimumMs("pageRefresh", timing);
  return { kind: "pageRefresh", startedAt: hold.startedAt, endsAt, minimumElapsed: now >= endsAt };
}

/** Entering or switching an organization; the workspace fades in once this hold lifts. */
export function isWorkspaceEntryHold(hold: LoaderHold | null): boolean {
  if (hold === null) return false;
  switch (hold.kind) {
    case "switcherToWorkspace":
    case "organizationSwitch":
      return true;
    case "loginToSwitcher":
    case "pageRefresh":
    case "loginPageRefresh":
      return false;
    default: {
      const unhandled: never = hold.kind;
      return unhandled;
    }
  }
}

/** Only a Login refresh holds a signed-out visitor; every other hold treats sign-out as done. */
export function loaderHoldCoversSignedOut(hold: LoaderHold | null): boolean {
  return hold?.kind === "loginPageRefresh";
}

/**
 * A 404/403 page confirms the destination only if it mounted after the hold
 * began. One still unmounting from the previous workspace says nothing about
 * the next one, so it must not end an organization switch early.
 */
export function confirmsErrorRoute(
  hold: LoaderHold | null,
  errorPageRegisteredAt: readonly number[],
): boolean {
  if (hold === null) return errorPageRegisteredAt.length > 0;
  return errorPageRegisteredAt.some((registeredAt) => registeredAt >= hold.startedAt);
}

export function isLoaderHoldActive(
  hold: LoaderHold | null,
  { destinationReady, failed, confirmedErrorRoute = false }: LoaderHoldConditions,
): boolean {
  if (hold === null || failed || confirmedErrorRoute) return false;
  return !hold.minimumElapsed || !destinationReady;
}

/**
 * Full-document entry. Returning from the Google redirect is a sign-in; a
 * reload of the Login page is a Login refresh; any other load (refresh, hard
 * refresh, history reload, a new tab) is a page load. A first visit to Login
 * is a page load, which never holds a signed-out visitor.
 */
export function documentEntryHoldKind(input: {
  navigationType: string | undefined;
  freshAuthentication: boolean;
  onLoginPage: boolean;
}): LoaderHoldKind {
  if (input.navigationType === "reload" && input.onLoginPage) return "loginPageRefresh";
  if (input.navigationType === "navigate" && input.freshAuthentication) return "loginToSwitcher";
  return "pageRefresh";
}
